import { useMemo } from 'react';

import {
  InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { API, endpoints } from '@/api/reports';
import { useReportPermissions } from '@/modules/analytics/hooks/useReportPermissions';
import { selectAccountId } from '@/redux/session/selectors';
import type {
  CreateReportParams,
  Report,
  ReportsListParams,
  ReportsListResponse,
} from '@/shared/types/reports';

type UseReportsParams = Omit<ReportsListParams, 'cursor'>;
type RenameReportVariables = { reportId: string; name: string };

export const useReports = (params: UseReportsParams = {}) => {
  const accountId = useSelector(selectAccountId);
  const { canRead } = useReportPermissions();

  const listKey = [endpoints.reports, accountId];

  const {
    data,
    status,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useInfiniteQuery<ReportsListResponse>({
    queryKey: [...listKey, params.name, params.limit, params.sort],
    queryFn: ({ pageParam }) =>
      API.listReports({ ...params, cursor: pageParam as string | undefined }),
    getNextPageParam: (lastPage) =>
      lastPage.pagination?.has_more ? lastPage.pagination.next_cursor : undefined,
    initialPageParam: undefined as string | undefined,
    enabled: !!accountId && canRead,
    placeholderData: keepPreviousData,
  });

  const reports = useMemo(() => data?.pages.flatMap((page) => page.reports) ?? [], [data?.pages]);
  const total = useMemo(() => data?.pages[0]?.pagination?.total, [data?.pages]);

  return {
    reports,
    total,
    status,
    error,
    fetchNextPage,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  };
};

export const useCreateReport = () => {
  const accountId = useSelector(selectAccountId);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const listKey = [endpoints.reports, accountId];

  return useMutation<Report, Error, CreateReportParams>({
    mutationFn: (body) => API.createReport(body),
    onSuccess: (created) => {
      queryClient.setQueriesData<InfiniteData<ReportsListResponse>>(
        { queryKey: listKey },
        (prev) =>
          prev && {
            ...prev,
            pages: prev.pages.map((page, i) =>
              i === 0
                ? {
                    ...page,
                    reports: [{ ...created, metrics_count: 0 }, ...page.reports],
                    pagination: page.pagination
                      ? { ...page.pagination, total: (page.pagination.total ?? 0) + 1 }
                      : page.pagination,
                  }
                : page
            ),
          }
      );
      queryClient.setQueryData([endpoints.report(created.report_id), accountId], created);
      toast.success(t('analytics.reports_created', { 0: created.name }));
    },
    meta: { errorToast: true },
  });
};

export const useRenameReport = () => {
  const accountId = useSelector(selectAccountId);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const listKey = [endpoints.reports, accountId];

  return useMutation<Report, Error, RenameReportVariables>({
    mutationFn: ({ reportId, name }) => API.renameReport(reportId, { name }),
    onSuccess: (updated) => {
      queryClient.setQueriesData<InfiniteData<ReportsListResponse>>(
        { queryKey: listKey },
        (prev) =>
          prev && {
            ...prev,
            pages: prev.pages.map((page) => ({
              ...page,
              reports: page.reports.map((r) =>
                r.report_id === updated.report_id ? { ...r, ...updated } : r
              ),
            })),
          }
      );
      queryClient.setQueryData([endpoints.report(updated.report_id), accountId], updated);
      toast.success(t('analytics.reports_renamed', { 0: updated.name }));
    },
    meta: { errorToast: true },
  });
};

export const useDeleteReport = () => {
  const accountId = useSelector(selectAccountId);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const listKey = [endpoints.reports, accountId];

  return useMutation<void, Error, string>({
    mutationFn: (reportId) => API.deleteReport(reportId),
    onSuccess: (_resp, reportId) => {
      queryClient.setQueriesData<InfiniteData<ReportsListResponse>>(
        { queryKey: listKey },
        (prev) =>
          prev && {
            ...prev,
            pages: prev.pages.map((page) => ({
              ...page,
              reports: page.reports.filter((r) => r.report_id !== reportId),
              pagination: page.pagination
                ? { ...page.pagination, total: Math.max(0, (page.pagination.total ?? 1) - 1) }
                : page.pagination,
            })),
          }
      );
      queryClient.removeQueries({ queryKey: [endpoints.report(reportId), accountId] });
      toast.success(t('analytics.reports_deleted'));
    },
    meta: { errorToast: true },
  });
};

type DuplicateReportVariables = { reportId: string; name: string };

export const useDuplicateReport = () => {
  const accountId = useSelector(selectAccountId);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<Report, Error, DuplicateReportVariables>({
    mutationFn: async ({ reportId, name }) => {
      const source = await queryClient.ensureQueryData<Report>({
        queryKey: [endpoints.report(reportId), accountId],
        queryFn: () => API.getReport(reportId),
      });
      const created = await API.createReport({ name });
      if (source.metrics.length === 0) {
        return created;
      }
      return API.updateReport(created.report_id, { metrics: source.metrics });
    },
    onSuccess: (created) => {
      queryClient.setQueryData([endpoints.report(created.report_id), accountId], created);
      toast.success(t('analytics.reports_duplicated', { 0: created.name }));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [endpoints.reports, accountId] });
    },
    meta: { errorToast: true },
  });
};
